import { saveUnifiedScene, SaveUnifiedSceneKeyframe, SaveUnifiedSceneModel } from 'visionary-core';
import * as THREE from 'three';
import { globalConfig, globalManager, modelDialogTools, globalTools } from '@/utils/globalConfig';
import { VisionaryScene } from '@/visionaryControllers/visionaryScene';
import { AnimationController } from '@/visionaryControllers/animationController';
import { SecureDirectoryPicker } from './filePicker';
import { getI18nText } from './commonFunc';
import { RightPanelTabs } from './commonParams';
import { PlyModel } from '@/uiControls/panelControls/heatmap-panel-tools';

export interface VideoExportOptions {
    resolution: { width: number, height: number }; // 分辨率
    fps: number; // 帧率
    bitrate: string; // 比特率
    duration: number; // 时长
    enableLineDrawing?: boolean; // 是否启用线稿处理
    enableExgs?: boolean; // 是否启用 EXGS 后处理
    exgsResolution?: { width: number; height: number }; // EXGS 指定分辨率
}
export class SceneUtils {
    // 新建空白场景
    async createNewScene() {
        globalTools?.timelinePanelTools?.handleAnimationPlay(false);
        const sceneInfo = globalManager.getCurrentSceneInfo(true);
        if (sceneInfo) {
            await sceneInfo.scene.resetScene();
            const enabled = globalConfig.lightSettings.skyboxSettings.enabled;
            sceneUtils.setSkyboxSettingsEnabled(enabled);
            globalTools.rightPanelTools?.setSkyboxEnabled(enabled);
        } else {
            console.error('场景信息不存在!!!');
        }
        if (globalTools && globalTools.timelinePanelTools) {
            globalTools.timelinePanelTools.updateTimelineWithKeyframes([], true); // 清空时间轴关键帧
        }
        const videoElement = document.querySelector('.recording-overlay-container')
        if (videoElement) {
            videoElement.remove();
        }
    }

    // 上传本地场景
    uploadLocalScene() {
        const sceneInfo = globalManager.getCurrentSceneInfo(true);
        if (sceneInfo) {
            this.openLocalScene();
        } else {
            console.error('场景信息不存在!!!');
        }
    }
    // 设置场景渲染模式 - 需要对接Core中的渲染模式
    setSceneRenderMode(mode: number) {
        const sceneInfos = globalManager.getAllSceneInfos();
        if (sceneInfos.length > 0) {
            sceneInfos.forEach(sceneInfo => {
                sceneInfo.scene.setRenderMode(mode);
            });
            globalConfig.currentRenderMode = mode;
        } else {
            console.error('场景信息不存在!!!');
        }
        if(mode === globalConfig.renderMode.heatmap){
            globalTools.rightPanelTools?.setActiveTab(RightPanelTabs.heatmap.id);
        }
        window.dispatchEvent(new CustomEvent('event.sceneRenderModeChange', { detail: mode }));
    }

    // 保存图片
    saveImage(width: number, height: number, isLineDrawing: boolean = false, viewWindow: 'left' | 'right' = 'left') {
        console.log('保存图片', width, height);
        globalTools?.timelinePanelTools?.handleAnimationPlay(false); // 停止动画播放
        const sceneInfos = globalManager.getAllSceneInfos();
        if (sceneInfos.length > 0) {
            // sceneInfos.forEach(sceneInfo => {
            //     sceneInfo.scene.exportImageAndDownload(width, height, isLineDrawing);
            // });
            if (viewWindow === 'left') {
                sceneInfos[0].scene.exportImageAndDownload(width, height, isLineDrawing);
            } else {
                if (sceneInfos.length > 1) {
                    sceneInfos[1].scene.exportImageAndDownload(width, height, isLineDrawing);
                } else {
                    console.error('场景信息不存在!!!');
                }
            }
        } else {
            console.error('场景信息不存在!!!');
        }
    }
    // 保存视频 
    saveVideo(options: VideoExportOptions, viewWindow: 'left' | 'right' = 'left') {
        console.log('[VideoExport][UI] sceneUtils.saveVideo 被调用（来自视频设置对话框），原始 options:', options);
        // 如果未设置 enableLineDrawing，默认为 false
        if (options.enableLineDrawing === undefined) {
            options.enableLineDrawing = false;
        }
        if (options.enableExgs === undefined) {
            options.enableExgs = false;
        }
        console.log('[VideoExport][UI] 归一化后的 VideoExportOptions:', options);
        const targetObject = globalManager.getCurrentObject();
        if (targetObject && targetObject.type === 'recordingCamera') {
            globalTools.timelinePanelTools?.handleAnimationPlay(false); // 停止动画播放，避免导出视频时动画影响
            const sceneInfos = globalManager.getAllSceneInfos();
            if (sceneInfos.length > 0) {

                if (viewWindow === 'left') {
                    sceneInfos[0].scene.exportVideoAndDownload(options, sceneInfos[0].animationController);
                } else {
                    if (sceneInfos.length > 1) {
                        sceneInfos[1].scene.exportVideoAndDownload(options, sceneInfos[1].animationController);
                    } else {
                        console.error('场景信息不存在!!!');
                    }
                }
            } else {
                console.error('场景信息不存在!!!');
            }
        } else {
            modelDialogTools.showMessageModal({
                title: getI18nText('message.exportVideo.title'),
                message: getI18nText('message.exportVideo.information'),
                duration: 0,
            });
            return;
        }
    }

    setModelAnimation(isAnimation: boolean) {
        globalConfig.sceneParams.isAnimation = isAnimation;
        window.dispatchEvent(new CustomEvent('event.modelAnimationChange', { detail: isAnimation }));
    }

    stopAnimation() {
        const sceneInfos = globalManager.getAllSceneInfos();
        if (sceneInfos.length > 0) {
            sceneInfos.forEach(sceneInfo => {
                if (globalConfig.sceneParams.isAnimation && typeof sceneInfo.scene.setGaussianAnimationTime === 'function') {
                    sceneInfo.scene.setGaussianAnimationTime(-1);
                }
            });
        }
    }
    // 切换到单视窗模式，弹出退出视窗对话框
    setSingleWindow(): boolean {
        console.log('setSingleWindow');
        modelDialogTools.openExitViewDialog();
        return false;
    }
    // 设置环境光颜色
    setAmbientLightColor(color: string) {
        globalConfig.lightSettings.ambientLight.color = color;
        const allInfos = globalManager.getAllSceneInfos();
        allInfos.forEach(sceneInfo => {
            sceneInfo.scene.setAmbientLightColor(color);
        });
    }

    // 设置环境光强度
    setAmbientLightIntensity(intensity: number) {
        globalConfig.lightSettings.ambientLight.intensity = intensity;
        const allInfos = globalManager.getAllSceneInfos();
        allInfos.forEach(sceneInfo => {
            sceneInfo.scene.setAmbientLightIntensity(intensity);
        });
    }

    // 设置平行光源颜色
    setDirectionalLightColor(color: string) {
        globalConfig.lightSettings.mainDirectionalLight.color = color;
        const allInfos = globalManager.getAllSceneInfos();
        allInfos.forEach(info => {
            info.scene.setDirectionalLightColor(color);
        });
    }
    // 设置平行光源强度
    setDirectionalLightIntensity(intensity: number) {
        globalConfig.lightSettings.mainDirectionalLight.intensity = intensity;
        const allInfos = globalManager.getAllSceneInfos();
        allInfos.forEach(info => {
            info.scene.setDirectionalLightIntensity(intensity);
        });
    }
    setDirectionalLightEnabled(enabled: boolean) {
        globalConfig.lightSettings.mainDirectionalLight.enabled = enabled;
        const allInfos = globalManager.getAllSceneInfos();
        allInfos.forEach(info => {
            info.scene.setDirectionalLightEnabled(enabled);
        });
    }
    setDirectionalLightPosition(position: { x: number; y: number; z: number }) {
        globalConfig.lightSettings.mainDirectionalLight.position = position;
        const allInfos = globalManager.getAllSceneInfos();
        allInfos.forEach(info => {
            info.scene.setDirectionalLightPosition(position.x, position.y, position.z);
        });
    }

    setSkyboxSettingsEnabled(enabled: boolean) {
        globalConfig.lightSettings.skyboxSettings.enabled = enabled;
        const allInfos = globalManager.getAllSceneInfos();
        allInfos.forEach(info => {
            info.scene.setSkyboxEnabled(enabled);
        });
    }

    setSkyboxSettingsUrl(url: string) {
        globalConfig.lightSettings.skyboxSettings.url = url;
        const allInfos = globalManager.getAllSceneInfos();
        allInfos.forEach(info => {
            void info.scene.setSkyboxUrl(url);
        });
    }

    setSkyboxSettingsMap(map: { front: string; back: string; left: string; right: string; top: string; bottom: string }) {
        
        const allInfos = globalManager.getAllSceneInfos();
        allInfos.forEach(info => {
            void info.scene.setSkyboxMap(map);
        });
    }
    setSkyboxSettingsSphereMap(map: string) {
        
        const allInfos = globalManager.getAllSceneInfos();
        allInfos.forEach(info => {
            void info.scene.setSkyboxSphereMap(map);
        });
    }

    ///////////   heatmap 相关方法     /////////////////////////////////////////////////////////////////////////
    setWeightOverlayBlendWithBase(blendWithBase: boolean) {
        const allInfos = globalManager.getAllSceneInfos();
        allInfos.forEach(info => {
            info.scene.setWeightOverlayBlendWithBase(blendWithBase);
        });
    }
    setWeightOverlayVisualOptions(options: { gain: number; colormap?: number }) {
        const allInfos = globalManager.getAllSceneInfos();
        allInfos.forEach(info => {
            info.scene.setWeightOverlayVisualOptions(options);
        });
    }

    updateHeatmapPipeline(plyModelMap: Map<string, PlyModel>) {
        const allInfos = globalManager.getAllSceneInfos();
        allInfos.forEach(info => {
            info.scene.updateHeatmapPipeline(plyModelMap.get(info.scene.id));
        });
    }

    disableHeatmapPipeline() {
        const allInfos = globalManager.getAllSceneInfos();
        allInfos.forEach(info => {
            info.scene.setHeatmapPipelineStatus(false);
        });
    }

    // 切换到多视窗模式
    async setMultiWindow(): Promise<boolean> {
        console.log('切换到多视窗模式');
        try {
            if (!globalTools?.mainViewTools) {
                console.error('mainViewTools 未初始化');
                return false;
            }
            await globalTools.mainViewTools.createNewPanel();
            const mainAppContainerDom = document.getElementById(globalConfig.domIds.mainAppContainer);
            if (mainAppContainerDom) { // 多视窗时，保存场景按钮不可用
                mainAppContainerDom.classList.add('multi-window-app-container');
            }
            return true;
        } catch (error) {
            console.error('创建新面板失败:', error);
            return false;
        }
    }

    // 保存场景
    async saveScene(folderHandle: FileSystemDirectoryHandle) {
        console.log('saveScene', folderHandle);
        (async () => {
            try {
                const selection = this.pickSceneViewForSave();
                if (!selection) {
                    return;
                }

                const { viewId, sceneView, sceneInfo } = selection;
                if (!sceneView || typeof sceneView.getObjects !== 'function') {
                    console.log('[Scene] save error', getI18nText('errorinfo.selectViewWindowError'));
                    return;
                }

                modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.savingScene'));

                // 在开始长时间异步操作前，验证文件夹句柄有效性
                const sceneFS = globalManager.getSceneFS();
                if (sceneFS && typeof sceneFS.ensurePermission === 'function') {
                    try {
                        await sceneFS.ensurePermission(folderHandle, 'readwrite');
                    } catch (error) {
                        console.error('[Scene] 文件夹权限验证失败:', error);
                        throw new Error(getI18nText('errorinfo.noPermission'));
                    }
                }
                const targetFolderHandle: FileSystemDirectoryHandle = folderHandle;

                const objects = sceneView.getObjects?.() || [];

                const ensureNumber = (value: any, fallback: number) => {
                    const num = Number(value);
                    return Number.isFinite(num) ? num : fallback;
                };

                const toTriplet = (source: any, fallback: [number, number, number]) => ([
                    ensureNumber(source?.x, fallback[0]),
                    ensureNumber(source?.y, fallback[1]),
                    ensureNumber(source?.z, fallback[2])
                ] as [number, number, number]);

                const cloneGaussianParams = (params: any) => {
                    if (params === null || params === undefined) return undefined;
                    try {
                        return JSON.parse(JSON.stringify(params));
                    } catch (error) {
                        if (typeof params === 'object') {
                            return { ...params };
                        }
                        return params;
                    }
                };

                const cloneRecordingCameraParams = (params: any) => {
                    if (params === null || params === undefined) return undefined;
                    try {
                        return JSON.parse(JSON.stringify(params));
                    } catch (error) {
                        if (typeof params === 'object') {
                            return { ...params };
                        }
                        return params;
                    }
                };

                const models: SaveUnifiedSceneModel[] = [];
                const modelsById = new Map<string, SaveUnifiedSceneModel>();
                const keyframeAccumulator = new Map<string, Map<number, { trs: [number[], number[], number[]]; gaussianParams?: any, cameraParams?: any }>>();

                const serializeMeshParams = (mesh: THREE.Mesh): Record<string, any> | undefined => {
                    if (!mesh) return undefined;
                    const geometry: any = (mesh as any).geometry;
                    const material: any = (mesh as any).material;

                    const geometryInfo: Record<string, any> = {};
                    if (geometry) {
                        geometryInfo.type = geometry.type;
                        if (geometry.parameters) {
                            try {
                                geometryInfo.parameters = JSON.parse(JSON.stringify(geometry.parameters));
                            } catch (error) {
                                geometryInfo.parameters = { ...geometry.parameters };
                            }
                        }
                    }

                    const materialInfo: Record<string, any> = {};
                    if (material) {
                        materialInfo.type = material.type;
                        if (material.color && typeof material.color.getHex === 'function') {
                            materialInfo.color = material.color.getHex();
                        }
                        if (typeof material.opacity === 'number') {
                            materialInfo.opacity = material.opacity;
                        }
                        if (typeof material.transparent === 'boolean') {
                            materialInfo.transparent = material.transparent;
                        }
                        if (material.wireframe) {
                            materialInfo.wireframe = !!material.wireframe;
                        }
                    }

                    const result: Record<string, any> = {};
                    if (Object.keys(geometryInfo).length) {
                        result.geometry = geometryInfo;
                    }
                    if (Object.keys(materialInfo).length) {
                        result.material = materialInfo;
                    }
                    return Object.keys(result).length ? result : undefined;
                };

                const serializeRecordingCameraParams = (object3d: THREE.Object3D): Record<string, any> | undefined => {
                    const recordingCamera: any = object3d?.userData?.recordingCamera;
                    if (!recordingCamera) return undefined;
                    const camera: any = recordingCamera.camera;
                    const params: Record<string, any> = {};
                    if (camera) {
                        params.camera = {
                            fov: ensureNumber(camera.fov, 55),
                            near: ensureNumber(camera.near, 0.1),
                            far: ensureNumber(camera.far, 1000),
                            aspect: ensureNumber(camera.aspect, 1)
                        };
                    }
                    const canvas = recordingCamera.canvas;
                    const width = ensureNumber(canvas?.width ?? recordingCamera.width, NaN);
                    const height = ensureNumber(canvas?.height ?? recordingCamera.height, NaN);
                    if (Number.isFinite(width) || Number.isFinite(height)) {
                        params.viewport = {
                            width: Number.isFinite(width) ? width : undefined,
                            height: Number.isFinite(height) ? height : undefined
                        };
                    }
                    if (typeof recordingCamera.isPreviewVisible === 'function') {
                        params.preview = {
                            visible: !!recordingCamera.isPreviewVisible()
                        };
                    }
                    if (typeof recordingCamera.getGizmoLength === 'function') {
                        params.gizmo = {
                            length: ensureNumber(recordingCamera.getGizmoLength(), 5)
                        };
                    }
                    return Object.keys(params).length ? params : undefined;
                };

                const resolveObjectSerialization = async (vo: any, object3d: THREE.Object3D) => {
                    const result: {
                        typeTag: string;
                        name?: string;
                        type?: string;
                        originFile?: File | Blob | FileSystemFileHandle;
                        assetName?: string;
                        params?: Record<string, any>;
                    } = {
                        typeTag: vo?.type || object3d?.type || 'object'
                    };

                    const file: File | undefined = vo?.file;
                    const fileHandle: FileSystemFileHandle | undefined = vo?.fileHandle || vo?.originFile;
                    const source = file || fileHandle;
                    const baseName = (file && file.name) || (fileHandle && (fileHandle as any).name) || vo?.name || object3d?.name;
                    const url: string = vo?.url || '';

                    if (source) {
                        const lname = typeof baseName === 'string' ? baseName.toLowerCase() : '';
                        let inferredType = vo?.type;
                        if (!inferredType && lname) {
                            if (lname.endsWith('.onnx')) {
                                inferredType = 'onnx';
                            } else if (lname.endsWith('.ply')) {
                                inferredType = 'ply';
                            }
                        }
                        result.typeTag = 'fileModel';
                        result.originFile = source;
                        result.assetName = baseName;
                        result.name = vo.name || baseName;
                        if (inferredType) {
                            result.type = inferredType;
                        }
                        return result;
                    }

                    if (url) { // 从 URL 读取文件并创建 Blob
                        try {
                            // 从 URL 获取文件名，如果 baseName 不存在则从 URL 中提取
                            let fileName = baseName;
                            if (!fileName || typeof fileName !== 'string') {
                                try {
                                    const urlObj = new URL(url);
                                    const urlFileName = urlObj.pathname.split('/').pop() || 'model';
                                    fileName = urlFileName;
                                } catch {
                                    // 如果不是有效的绝对 URL，尝试从路径中提取文件名
                                    const urlFileName = url.split('/').pop() || url.split('\\').pop() || 'model';
                                    fileName = urlFileName;
                                }
                            }

                            // 从 URL 读取文件流
                            console.log('[Scene] 正在从 URL 下载文件:', url);
                            const response = await fetch(url);
                            if (!response.ok) {
                                throw new Error(`从 URL 获取文件失败: ${response.status} ${response.statusText}`);
                            }

                            // 获取文件内容为 Blob
                            const blob = await response.blob();

                            // 从响应头或文件名获取 MIME 类型
                            let mimeType = blob.type;
                            if (!mimeType || mimeType === 'application/octet-stream') {
                                const lname = fileName.toLowerCase();
                                if (lname.endsWith('.onnx')) {
                                    mimeType = 'application/octet-stream';
                                } else if (lname.endsWith('.ply')) {
                                    mimeType = 'model/ply';
                                } else if (lname.endsWith('.glb')) {
                                    mimeType = 'model/gltf-binary';
                                } else if (lname.endsWith('.gltf')) {
                                    mimeType = 'model/gltf+json';
                                }
                            }

                            // 创建 File 对象，使用 baseName 作为文件名
                            const fileFromUrl = new File([blob], fileName, { type: mimeType || blob.type });

                            const lname = fileName.toLowerCase();
                            let inferredType = vo?.type;
                            if (!inferredType && lname) {
                                if (lname.endsWith('.onnx')) {
                                    inferredType = 'onnx';
                                } else if (lname.endsWith('.ply')) {
                                    inferredType = 'ply';
                                } else if (lname.endsWith('.glb') || lname.endsWith('.gltf')) {
                                    inferredType = 'ply'; // 或其他合适的类型
                                }
                            }

                            result.typeTag = 'fileModel';
                            result.originFile = fileFromUrl;
                            result.assetName = fileName;
                            result.name = fileName;
                            if (inferredType) {
                                result.type = inferredType;
                            }

                            console.log('[Scene] 成功从 URL 创建文件:', fileName);
                            return result;
                        } catch (error) {
                            console.error('[Scene] 从 URL 读取文件失败:', error);
                            // 抛出错误以便上层处理
                            throw error;
                        }
                    }

                    if (vo?.type === 'recordingCamera' || object3d?.userData?.type === 'recordingCamera') {
                        result.typeTag = 'recordingCamera';
                        result.name = vo?.name || 'RecordingCamera';
                        result.type = 'recordingCamera';
                        result.params = serializeRecordingCameraParams(object3d);
                        return result;
                    }

                    if (object3d instanceof THREE.Mesh) {
                        const geometryType = (object3d.geometry && object3d.geometry.type) || 'Mesh';
                        if (geometryType === 'BoxGeometry') {
                            result.typeTag = 'primitive.box';
                        } else if (geometryType === 'SphereGeometry') {
                            result.typeTag = 'primitive.sphere';
                        } else {
                            result.typeTag = 'three.mesh';
                        }
                        result.name = vo?.name || object3d.name || geometryType;
                        result.type = 'mesh';
                        const params = serializeMeshParams(object3d);
                        if (params) {
                            result.params = params;
                        }
                        return result;
                    }

                    result.name = vo?.name || object3d?.name || `object_${vo?.id || ''}`;
                    result.type = vo?.type || object3d?.type;
                    const userDataType = object3d?.userData?.type;
                    if (userDataType && !result.params) {
                        result.params = { userDataType };
                    }
                    return result;
                };

                for (const vo of objects) {
                    if (!vo) continue;
                    const object3d = vo.object;
                    if (!object3d) continue;
                    let serialization;
                    try {
                        serialization = await resolveObjectSerialization(vo, object3d);
                        console.log("#############serialization:", serialization,vo,object3d);
                    } catch (error) {
                        console.error('[Scene] 序列化对象失败:', vo, error);
                        continue;
                    }
                    if (!serialization || !serialization.typeTag) {
                        console.warn('[Scene] 未识别的对象类型，跳过保存:', vo);
                        continue;
                    }

                    const trs: [number[], number[], number[]] = [
                        toTriplet(object3d?.position, [0, 0, 0]),
                        toTriplet(object3d?.rotation, [0, 0, 0]),
                        toTriplet(object3d?.scale, [1, 1, 1])
                    ];

                    const gaussianParams = cloneGaussianParams(sceneView.getModelGaussianParams?.(vo.id));
                    const cameraParams = cloneRecordingCameraParams(sceneView.getRecordingCameraParams?.(vo.id));

                    const modelEntry: SaveUnifiedSceneModel = {
                        id: vo.id,
                        name: serialization.name,
                        typeTag: serialization.typeTag,
                        trs,
                        type: serialization.type,
                        originFile: serialization.originFile,
                        assetName: serialization.assetName,
                        params: serialization.params
                    };
                    if (gaussianParams !== undefined) {
                        modelEntry.gaussianParams = gaussianParams;
                    }
                    if (cameraParams !== undefined) {
                        modelEntry.name = modelEntry.name?.replace('(新)', '').replace('(New)', '');
                        modelEntry.cameraParams = cameraParams;
                    }
                    models.push(modelEntry);
                    modelsById.set(vo.id, modelEntry);

                    const frameMap = new Map<number, { trs: [number[], number[], number[]]; gaussianParams?: any, cameraParams?: any }>();
                    keyframeAccumulator.set(vo.id, frameMap);
                }

                const animationController = sceneInfo?.animationController;
                if (animationController && typeof animationController.getGlobalKeyframes === 'function') {
                    const globalKeyframes = animationController.getGlobalKeyframes() || [];
                    for (const frameInfo of globalKeyframes) {
                        if (!frameInfo || typeof frameInfo !== 'object') continue;
                        const frameIndexRaw = (frameInfo as any).index ?? (frameInfo as any).position;
                        const frameIndex = Number(frameIndexRaw);
                        if (!Number.isFinite(frameIndex)) continue;
                        const frameObjects = (frameInfo as any).objects || [];
                        for (const objFrame of frameObjects) {
                            if (!objFrame || typeof objFrame !== 'object') continue;
                            const objectId = objFrame.objectId;
                            if (!objectId) continue;
                            if (!keyframeAccumulator.has(objectId)) {
                                if (modelsById.has(objectId)) {
                                    const modelMeta = modelsById.get(objectId)!;
                                    const map = new Map<number, { trs: [number[], number[], number[]]; gaussianParams?: any, cameraParams?: any }>();
                                    keyframeAccumulator.set(objectId, map);
                                } else {
                                    continue;
                                }
                            }
                            const trs: [number[], number[], number[]] = [
                                toTriplet(objFrame.position, [0, 0, 0]),
                                toTriplet(objFrame.rotation, [0, 0, 0]),
                                toTriplet(objFrame.scale, [1, 1, 1])
                            ];
                            // const gaussianParams = cloneGaussianParams(objFrame.gaussianParams);
                            const cameraParams = cloneRecordingCameraParams(objFrame.cameraParams);
                            const frameMap = keyframeAccumulator.get(objectId)!;
                            const existing = frameMap.get(frameIndex);
                            // if (!existing || JSON.stringify(existing.trs) !== JSON.stringify(trs) || existing.gaussianParams !== gaussianParams || existing.cameraParams !== cameraParams) {
                            //     frameMap.set(frameIndex, { trs, gaussianParams, cameraParams });
                            // }
                            if (!existing || JSON.stringify(existing.trs) !== JSON.stringify(trs) || existing.cameraParams !== cameraParams) {
                                frameMap.set(frameIndex, { trs, gaussianParams: undefined, cameraParams });
                            }
                        }
                    }
                }

                const keyframes: SaveUnifiedSceneKeyframe[] = [];
                keyframeAccumulator.forEach((framesMap, objectId) => {
                    const sorted = Array.from(framesMap.entries()).sort((a, b) => a[0] - b[0]);
                    sorted.forEach(([frame, payload]) => {
                        if (frame < 0) return;
                        if (!Number.isFinite(frame)) return;
                        const entry: SaveUnifiedSceneKeyframe = {
                            objectId,
                            frame,
                            trs: payload.trs
                        };
                        if (payload.cameraParams !== undefined) {
                            entry.cameraParams = payload.cameraParams;
                        }
                        keyframes.push(entry);
                    });
                });
                const cameraParams = sceneInfo?.scene?.getCameraParams();
                const skyboxSettings = sceneInfo?.scene?.getSkyboxSettings();
                await saveUnifiedScene({
                    skyboxEnabled: skyboxSettings.enabled,
                    // skyboxUrl: skyboxSettings.url,
                    cameraParams: cameraParams,
                    totalFrames: globalConfig.totalFrames || 120,
                    scenes: [
                        {
                            view: viewId,
                            models,
                            keyframes
                        }
                    ],
                    folderHandle: targetFolderHandle,
                    meta: { app: 'VisionaryEditor', savedAt: new Date().toISOString() }
                });

                console.log('[Scene] 场景保存完成');
                modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.sceneSaved'));
            } catch (e) {
                console.error('[Scene] 保存场景失败:', e);
                alert(getI18nText('errorinfo.saveSceneFailed', { error: (e as Error).message }));
            } finally {
                modelDialogTools.closeLoadingInfoModal();
            }
        })();
    }
    // 处理打开场景 -- 需要重构，去sceneFS化
    public handleOpenScene(): void {
        globalTools?.timelinePanelTools?.handleAnimationPlay(false); // 停止动画播放
        const openSceneTitle = getI18nText('message.openScene.title');
        const openSceneConfirm = getI18nText('message.openScene.confirm');
        modelDialogTools.showConfirmDialog(
            openSceneTitle,
            openSceneConfirm,
            () => {
                this.openLocalScene();
            },
            () => {
                // console.log('用户取消打开场景');
            }
        );
    }

    async openLocalScene() {
        const sceneFS = globalManager.getSceneFS();
        if (!sceneFS) {
            console.warn('[Scene] SceneFS 未初始化，无法打开场景');
            alert(getI18nText('errorinfo.sceneFSNotAvailable'));
            return;
        }
        // 不阻塞 showDirectoryPicker：统计放到微任务，避免 gtag 同步脚本拖慢系统目录对话框弹出
        const gtagTrack = (window as unknown as { gtagTracking?: (c: string, l: string, i: string) => void }).gtagTracking;
        if (typeof gtagTrack === 'function') {
            queueMicrotask(() => gtagTrack('click', 'openScene', 'openScene'));
        }
        try {
            const picker = new SecureDirectoryPicker();
            const handle = await picker.show({ mode: 'read' }).catch(console.error);
            if (!handle) {
                console.error('No directory handle returned');
                return;
            }
            const sceneFSCompat = sceneFS as unknown as {
                setRootHandle?: (rootHandle: FileSystemDirectoryHandle, mode: 'read' | 'readwrite') => Promise<void>;
                ensurePermission?: (rootHandle: FileSystemDirectoryHandle, mode: 'read' | 'readwrite') => Promise<void>;
                rootHandle?: FileSystemDirectoryHandle;
                permissions?: 'read' | 'readwrite' | null;
            };
            if (typeof sceneFSCompat.setRootHandle === 'function') {
                await sceneFSCompat.setRootHandle(handle as FileSystemDirectoryHandle, 'read');
            } else {
                if (typeof sceneFSCompat.ensurePermission === 'function') {
                    await sceneFSCompat.ensurePermission(handle as FileSystemDirectoryHandle, 'read');
                }
                // 兼容新版 SceneFS（无 setRootHandle 公共方法）
                sceneFSCompat.rootHandle = handle as FileSystemDirectoryHandle;
                sceneFSCompat.permissions = 'read';
            }
            // 文件夹选择成功后，继续加载场景
            this.loadScene();

        } catch (e) {
            if ((e as Error).message.includes('cancelled')) {
                console.log('[Scene] 用户取消了文件夹选择');
            } else {
                console.warn('[Scene] 选择场景目录失败:', e);
                alert(getI18nText('errorinfo.selectSceneDirectoryFailed', { error: (e as Error).message }));
            }
        }
    }

    async loadScene() {
        console.log('执行打开场景操作');
        console.log('globalTools', globalManager.getCurrentSceneInfo(true));
        const sceneFS = globalManager.getSceneFS();
        if (!sceneFS) {
            console.warn('[Scene] SceneFS 未初始化，无法打开场景');
            alert(getI18nText('errorinfo.sceneFSNotAvailable'));
            return;
        }
        (async () => {
            try {
                // 检查是否已经选择了文件夹
                if (!sceneFS.hasFolder()) {
                    console.warn('[Scene] 未选择场景目录，尝试选择...');
                    try {
                        await sceneFS.pickFolderRead();
                    } catch (e) {
                        console.warn('[Scene] 选择场景目录失败:', e);
                        return;
                    }
                }
                const sceneInfo = globalManager.getCurrentSceneInfo(true);
                if (sceneInfo) {
                    await sceneInfo.scene.resetScene(false);
                } else {
                    console.error('场景信息不存在!!!');
                    modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.sceneInfoNotExists'));
                    setTimeout(() => {
                        modelDialogTools.closeLoadingInfoModal();
                    }, 1000 * 3);
                    return;
                }
                modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.loadingSceneFile'));
                const app = globalManager.getAppInstance();
                if (!app) {
                    console.warn('[Scene] 未找到兼容的 App 实例，无法将 scene.json 加载到运行时');
                    alert(getI18nText('errorinfo.runtimeAppNotDetected'));
                    return;
                }
                console.log('sceneFS.loadScene:', app.sceneData);
                const sceneData = await sceneFS.loadScene(app);
                console.log('[Scene] 场景加载完成', sceneData);
                sceneUtils.setModelAnimation(globalConfig.sceneParams.isAnimation); // 统一设置动画预览状态，同步加载的模型，避免加载模型时动画状态不一致

                if (sceneData && sceneData.camera) {
                    console.log('sceneData.camera:', sceneData.camera);
                    console.log('globalTools.rightPanelTools.onUpdateCamera:', globalTools.rightPanelTools);
                    globalTools.rightPanelTools?.onUpdateCamera(sceneData.camera);
                }
                if (sceneData) {
                    const enabled = typeof sceneData.skyboxEnabled === 'boolean' ? sceneData.skyboxEnabled : true; // 默认加载
                    sceneUtils.setSkyboxSettingsEnabled(enabled);

                    globalTools.rightPanelTools?.setSkyboxEnabled(enabled);
                }
                if (sceneData && sceneData.totalFrames) {
                    globalTools?.timelinePanelTools?.setTotalFrames(sceneData.totalFrames);
                }
                modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.sceneLoaded'));
            } catch (e) {
                console.error('[Scene] 打开场景失败:', e);
                alert(getI18nText('errorinfo.openSceneFailed', { error: (e as Error).message }));
            } finally {
                modelDialogTools.closeLoadingInfoModal();
            }

        })();
    }

    // 从json中加载场景
    async loadSceneByJson(sceneData: any, scene: VisionaryScene, animationController: AnimationController) {
        console.log('执行打开场景操作 by json');
        if (!scene || !animationController) {
            console.error('场景或动画控制器不存在!!!');
            return;
        }
        await scene.resetScene(false);
        await animationController.replaceGlobalKeyframes([]);

        for (const model of sceneData.models) {
            modelDialogTools.showLoadingInfoModal('正在加载模型:' + model.name);
            if (!model) continue;
            if (model.typeTag === 'recordingCamera') {
                scene.restoreRecordingCameraFromSerialized(model.id, model.name, Array.isArray(model.trs) ? model.trs : undefined, model.params);
            } else if (model.typeTag === 'url') {
                await this.loadModelFromUrl(model, scene);
                if (Array.isArray(model.trs) && typeof scene.applyTRSToObject === 'function') {
                    scene.applyTRSToObject(model.id, model.trs);
                } else {
                    console.warn('未知或暂不支持的对象类型标签', model.typeTag, model);
                }
            } else if (model.typeTag === 'fileModel') {
                console.log('线上场景，不支持文件模式')
                continue;
            } else {
                scene.restorePrimitiveFromSerialized(model.typeTag, model.id, model.name, model.params);
                if (Array.isArray(model.trs) && typeof scene.applyTRSToObject === 'function') {
                    scene.applyTRSToObject(model.id, model.trs);
                } else {
                    console.warn('未知或暂不支持的对象类型标签', model.typeTag, model);
                }
            }
        }
        modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.syncingKeyframes'));
        if (sceneData.totalFrames) { // 同步总帧数
            globalTools?.timelinePanelTools?.setTotalFrames(sceneData.totalFrames);
        }
        for (const keyframe of sceneData.keyframes) {
            await animationController.loadKeyframesFromSerialized(sceneData.keyframes);
        }
        console.log('[Scene] 场景加载完成');
        sceneUtils.setModelAnimation(globalConfig.sceneParams.isAnimation); // 统一设置动画预览状态，同步加载的模型，避免加载模型时动画状态不一致
        if (sceneData.camera) {
            console.log('sceneData.camera:', sceneData.camera);
            console.log('globalTools.rightPanelTools.onUpdateCamera:', globalTools.rightPanelTools);
            globalTools.rightPanelTools?.onUpdateCamera(sceneData.camera);
        }
        modelDialogTools.showLoadingInfoModal('场景加载完成');
        modelDialogTools.closeLoadingInfoModal(0.5);
    }

    async loadModelFromUrl(model: any, scene: VisionaryScene) {
        if (!model || !scene) return;
        const forcedId = model?.id;
        const displayName = model?.name;
        const url = model?.assetName;

        if (scene && typeof scene.loadModel === 'function') {
            try {
                if (!url) {
                    console.error('[Scene] 模型URL不存在!!!');
                    throw new Error('模型URL不存在!!!');
                }
                console.log('[Scene] 加载模型:', url);
                modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.loadingOnlineModel'));
                const options: Record<string, any> = {};
                if (forcedId) options.forcedId = forcedId;
                if (displayName) options.displayName = displayName;
                let lower = url.toLowerCase();
                if (lower.endsWith('.onnx')) {
                    options.expectedType = 'onnx';
                } else if (lower.endsWith('.ply') || lower.endsWith('.glb') || lower.endsWith('.gltf')) {
                    options.expectedType = 'ply';
                } else {
                    options.expectedType = 'url';
                }
                options.notCloseLoading = true;
                await scene.loadModel(url, options);
            } catch (e) {
                console.error('[Scene] 加载模型失败:', e);
                throw new Error('加载模型失败：' + (e as Error).message);
            }
        }
    }

    resetEnvironment() {
        this.setSceneRenderMode(globalConfig.renderMode.normal); // 设置场景渲染模式为普通
        if (globalTools) {
            if (globalTools.timelinePanelTools) { // 清空时间轴关键帧
                globalTools.timelinePanelTools.updateTimelineWithKeyframes([], true);
            }
            // onReset
            if (globalTools.rightPanelTools) { // 重置右侧面板设置参数
                globalTools.rightPanelTools.onResetAll();
            }
        }

    }

    private findSceneInfoByScene(sceneInstance: any) {
        const allInfos = globalManager.getAllSceneInfos();
        return allInfos.find(info => info.scene === sceneInstance) || null;
    }

    private pickSceneViewForSave(): { viewId: 'left' | 'right', sceneView: any, sceneInfo: any } | null {
        const mainViewTools = globalTools?.mainViewTools;
        if (!mainViewTools) {
            alert(getI18nText('errorinfo.mainViewToolsNotDetected'));
            return null;
        }

        const candidateViews: Array<{ viewId: 'left' | 'right', sceneView: any }> = [];
        const leftTools = mainViewTools.getLeftSceneViewTools?.();
        const rightTools = mainViewTools.getRightSceneViewTools?.();

        const leftScene = leftTools?.getSceneView?.();
        if (leftScene) {
            candidateViews.push({ viewId: 'left', sceneView: leftScene });
        }

        const rightScene = rightTools?.getSceneView?.();
        if (rightScene) {
            candidateViews.push({ viewId: 'right', sceneView: rightScene });
        }

        if (!candidateViews.length) {
            alert(getI18nText('errorinfo.noSaveableSceneView'));
            return null;
        }

        let selected = candidateViews[0];

        if (candidateViews.length > 1) {
            const defaultView = candidateViews[0].viewId;
            const input = window.prompt('当前为双视窗模式，请输入要保存的视窗（left 或 right，默认为 left）', defaultView);
            if (input === null) {
                console.log('[Scene] 用户取消了视窗选择');
                return null;
            }
            const normalized = input.trim().toLowerCase();
            if (normalized === 'right') {
                const rightCandidate = candidateViews.find(v => v.viewId === 'right');
                if (!rightCandidate) {
                    alert(getI18nText('errorinfo.rightViewWindowNotDetected'));
                    return null;
                }
                selected = rightCandidate;
            } else if (normalized === 'left' || normalized === '') {
                const leftCandidate = candidateViews.find(v => v.viewId === 'left');
                if (leftCandidate) {
                    selected = leftCandidate;
                }
            } else {
                alert(getI18nText('errorinfo.invalidInputLeftOrRight'));
                return null;
            }
        }

        const sceneInfo = this.findSceneInfoByScene(selected.sceneView);
        return { viewId: selected.viewId, sceneView: selected.sceneView, sceneInfo };
    }
}

// 创建并导出单例实例
const sceneUtils = new SceneUtils();
export { sceneUtils };
